// Per-state insights derived from grants + partners + districts
// Returns: sanctionedCr, disbursedCr, grants count, grantees (unique partners),
// districts count, blocks (mock), portfolio breakdown (per pillar),
// avgScore (out of 5), sentiment (negative/neutral/positive %), voiceSamples count

import { STATE_REACH } from "./states";
import { DISTRICT_REACH } from "./districts";
import { GRANTS } from "./grants";
import { PARTNERS } from "./partners";

const partnerLookup = PARTNERS.reduce((acc, p) => { acc[p.id] = p; return acc; }, {});

// Deterministic "blocks" estimate
function blocksFor(state) {
  const b = state.beneficiaries;
  return Math.max(4, Math.round(b / 250));
}

function disbursedFromSanctioned(sanctioned, state) {
  const hash = (state.state || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const ratio = 0.18 + ((hash % 37) / 100);
  return +(sanctioned * ratio).toFixed(2);
}

// Deterministic sentiment distribution per name — skewed positive, varies by hash.
// Returns { positive, neutral, negative, samples }
function sentimentFor(name, scaleHint = 1) {
  const hash = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0) * 7, 0);
  const samples = Math.max(8, Math.round(20 * scaleHint + (hash % 60)));
  // Positive 50-78%, Neutral 15-32%, Negative 5-22% (deterministic from hash)
  const pos = 50 + (hash % 29);                  // 50..78
  const neg = 5 + ((hash * 3) % 18);              // 5..22
  const neu = Math.max(0, 100 - pos - neg);
  return {
    positive: pos,
    neutral:  neu,
    negative: neg,
    samples,
  };
}

function avgScoreOf(grants) {
  if (!grants.length) return null;
  const sum = grants.reduce((s, g) => s + g.score, 0);
  return +(sum / grants.length).toFixed(2);
}

export function getStateInsights(stateName) {
  const state = STATE_REACH.find((s) => s.state === stateName);
  if (!state) return null;

  const grants = GRANTS.filter((g) => {
    const p = partnerLookup[g.partnerId];
    return p && p.states.includes(stateName);
  });

  const sanctioned = grants.reduce((sum, g) => {
    const p = partnerLookup[g.partnerId];
    const share = p && p.states.length ? g.fundedCr / p.states.length : 0;
    return sum + share;
  }, 0);

  const grantees = new Set(grants.map((g) => g.partnerId)).size;

  const portfolio = { employability: 0, entrepreneurship: 0, environment: 0 };
  grants.forEach((g) => {
    const p = partnerLookup[g.partnerId];
    if (p && portfolio[p.pillar] !== undefined) portfolio[p.pillar] += 1;
  });

  return {
    state: state.state,
    sanctionedCr: +sanctioned.toFixed(2),
    disbursedCr: disbursedFromSanctioned(sanctioned, state),
    grantsCount: grants.length,
    granteesCount: grantees,
    districts: state.districts,
    districtSampleCount: (DISTRICT_REACH[state.state] || []).length,
    blocks: blocksFor(state),
    portfolio,
    pillars: state.pillars,
    beneficiaries: state.beneficiaries,
    avgScore: avgScoreOf(grants),
    sentiment: sentimentFor(state.state, Math.max(1, grants.length / 4)),
  };
}

export function getDistrictInsights(districtName, stateName) {
  const state = STATE_REACH.find((s) => s.state === stateName);
  if (!state) return null;
  const districtList = DISTRICT_REACH[stateName] || [];
  const district = districtList.find((d) => d.district === districtName);
  if (!district) return null;

  const stateInsights = getStateInsights(stateName);
  if (!stateInsights) return null;

  const districtShare = state.beneficiaries
    ? district.beneficiaries / state.beneficiaries
    : 0;

  const sanctioned = +(stateInsights.sanctionedCr * districtShare).toFixed(2);
  const disbursed  = +(stateInsights.disbursedCr  * districtShare).toFixed(2);
  const grants    = Math.max(1, Math.round(stateInsights.grantsCount * districtShare));
  const grantees  = Math.max(1, Math.min(grants, Math.round(stateInsights.granteesCount * districtShare)));

  const portfolio = { employability: 0, entrepreneurship: 0, environment: 0 };
  let remaining = grants;
  const sortedPillars = Object.entries(stateInsights.portfolio)
    .filter(([, c]) => c > 0)
    .sort((a, b) => b[1] - a[1]);
  for (let i = 0; i < sortedPillars.length; i++) {
    const [pid, count] = sortedPillars[i];
    if (i === sortedPillars.length - 1) {
      portfolio[pid] = remaining;
    } else {
      const take = Math.min(remaining, Math.max(1, Math.round(grants * (count / stateInsights.grantsCount))));
      portfolio[pid] = take;
      remaining -= take;
    }
  }

  const blocks = Math.max(1, Math.round(district.beneficiaries / 150));

  return {
    isDistrict: true,
    state: stateName,
    district: districtName,
    sanctionedCr: sanctioned,
    disbursedCr: disbursed,
    grantsCount: grants,
    granteesCount: grantees,
    beneficiaries: district.beneficiaries,
    blocks,
    portfolio,
    avgScore: stateInsights.avgScore,   // inherit state avg (mock)
    sentiment: sentimentFor(districtName + stateName, Math.max(1, district.beneficiaries / 200)),
  };
}


// Build the full lookup once for use in the map's color scale
export const STATE_SANCTIONED = STATE_REACH.map((s) => {
  const i = getStateInsights(s.state);
  return { state: s.state, sanctionedCr: i?.sanctionedCr || 0 };
});

export const TOTAL_SANCTIONED_CR = STATE_SANCTIONED.reduce((s, x) => s + x.sanctionedCr, 0);
